import { Link } from "react-router-dom";
import {
  ArrowRight,
  Plane,
  Users,
  Trophy,
  Map as MapIcon,
  Car,
  CheckCircle2,
} from "lucide-react";
import Seo from "@/components/Seo";
import { DESTINATION_IMAGES } from "@/lib/media";

const BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "Dubai DMC", path: "/dubai-dmc" },
];

const SERVICES = [
  {
    icon: Trophy,
    title: "MICE Management",
    copy:
      "Full-service meetings, incentives, conferences, and exhibitions across Dubai's world-class venues.",
  },
  {
    icon: Users,
    title: "Corporate Group Travel",
    copy:
      "Tailored programmes with dedicated account managers and 24/7 on-ground support.",
  },
  {
    icon: Trophy,
    title: "Incentive Travel",
    copy:
      "Bespoke programmes designed to reward and motivate top performers.",
  },
  {
    icon: MapIcon,
    title: "Leisure & FIT",
    copy:
      "Customised itineraries for groups and free independent travellers.",
  },
  {
    icon: Car,
    title: "Ground Transfers",
    copy: "Luxury fleet, VIP meet-and-greet, and seamless logistics.",
  },
];

const DIFFERENCE = [
  "20+ years of experience in the GCC and Southeast Asia",
  "140,000+ travellers managed",
  "Local Dubai team with 24/7 support",
  "Strong relationships with top hotels and venues",
  "Full event production capabilities",
];

const FAQS = [
  {
    q: "What is a Destination Management Company (DMC) in Dubai?",
    a: "A DMC is a professional services company with deep local expertise that designs and delivers travel programmes, events, and logistics for corporate and leisure groups. In Dubai, Trawelldmc handles everything from venue selection and hotel negotiation to full event production and ground transport.",
  },
  {
    q: "What types of groups does Trawelldmc handle in Dubai?",
    a: "We serve corporate groups, MICE delegates, incentive winners, and leisure tour groups ranging from 10 to 1,000+ participants.",
  },
  {
    q: "How far in advance should I book a DMC in Dubai?",
    a: "For large MICE events, 6–12 months in advance is recommended. For smaller corporate groups, 4–6 weeks is typically sufficient.",
  },
  {
    q: "Does Trawelldmc handle visa arrangements for Dubai?",
    a: "Yes — we assist with visa documentation guidance and can coordinate group visa support through our network.",
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function DubaiDmc() {
  return (
    <div data-testid="dubai-dmc-page">
      <Seo
        path="/dubai-dmc"
        title="DMC Dubai — Destination Management Company | Trawelldmc"
        description="Looking for a trusted DMC in Dubai? Trawelldmc delivers MICE, corporate, and incentive travel management. 20+ years of expertise. 140,000+ travellers. Get a custom proposal today."
        breadcrumb={BREADCRUMB}
        extraJsonLd={FAQ_JSONLD}
      />

      {/* HERO */}
      <section className="relative">
        <img
          src={DESTINATION_IMAGES.dubai}
          alt="Dubai MICE and corporate travel — Trawelldmc DMC"
          width="1920"
          height="1080"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(11,25,44,0.92) 0%, rgba(11,25,44,0.55) 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-32 md:py-44 text-white">
          <div className="max-w-3xl">
            <div className="overline !text-[#FF5238] mb-5">
              Dubai DMC · Headquartered in UAE
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.06]">
              <span className="text-[#FF5238]">Dubai</span> Destination Management Company (DMC) & MICE Specialist
            </h1>
            <p className="text-white/80 mt-8 text-lg max-w-2xl leading-relaxed">
              MICE, corporate, and incentive travel — engineered end-to-end by
              the team that's served 140,000+ travellers over two decades.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/inquiry?destination=Dubai"
                className="btn-primary"
                data-testid="dubai-dmc-cta-proposal"
              >
                Get a custom proposal <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/quote"
                className="btn-secondary"
                data-testid="dubai-dmc-cta-ai-quote"
              >
                Instant AI estimate
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHY DUBAI */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Why Dubai</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Why Choose a DMC in Dubai?
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-slate-700 text-lg leading-relaxed">
              Dubai is one of the world's leading MICE and corporate travel
              destinations. As a premier DMC in Dubai, Trawelldmc brings 20+
              years of expertise and 140,000+ satisfied travellers. We handle
              every detail — airport transfers, hotel contracting, gala
              dinners, team-building, and full conference logistics — so your
              group has a seamless experience.
            </p>
            <p className="text-slate-600 mt-6 leading-relaxed">
              From a 40-pax board offsite at a Palm Jumeirah resort to a
              2,500-pax dealer conference at the Coca-Cola Arena, our local
              operations team owns every contract, every transfer, every
              moment on the ground. You focus on your delegates — we handle
              everything else.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/destinations/dubai"
                className="inline-flex items-center gap-2 text-[#0B192C] font-medium border-b-2 border-[#FF5238] pb-1 hover:gap-3 transition-all"
                data-testid="dubai-dmc-explore-packages"
              >
                Explore our Dubai packages <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline mb-4">What we deliver</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Our Dubai DMC Services
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s) => (
              <div
                key={s.title}
                className="card-flat p-8"
                data-testid={`dubai-service-${s.title
                  .split(" ")[0]
                  .toLowerCase()}`}
              >
                <s.icon className="h-8 w-8 text-[#FF5238]" />
                <h3 className="font-heading text-xl font-medium tracking-tight text-[#0B192C] mt-5">
                  {s.title}
                </h3>
                <p className="text-slate-600 mt-3 leading-relaxed">{s.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DIFFERENCE */}
      <section className="bg-[#0B192C] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="overline !text-[#FF5238] mb-4">
                The Trawelldmc edge
              </div>
              <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight">
                The Trawelldmc <br />
                <span className="text-[#FF5238]">Difference.</span>
              </h2>
            </div>
            <ul className="lg:col-span-7 space-y-5">
              {DIFFERENCE.map((d, i) => (
                <li
                  key={d}
                  className="flex gap-4 items-start border-t border-white/15 pt-5"
                  data-testid={`dubai-difference-${i}`}
                >
                  <CheckCircle2 className="h-6 w-6 text-[#FF5238] flex-shrink-0 mt-0.5" />
                  <span className="text-lg text-white/90 leading-snug">
                    {d}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">FAQ</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
            Frequently Asked Questions
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
          {FAQS.map((f, i) => (
            <div key={f.q} data-testid={`dubai-faq-${i}`}>
              <h3 className="font-heading text-xl font-medium text-[#0B192C] tracking-tight">
                {f.q}
              </h3>
              <p className="text-slate-600 mt-3 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#FDFCF8] border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-4">Next step</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Ready to plan your <br />
              <span className="text-[#FF5238]">Dubai programme?</span>
            </h2>
            <p className="text-slate-600 mt-6 max-w-xl text-lg leading-relaxed">
              Contact Trawelldmc for a custom proposal. Our Dubai team responds
              within 24 hours.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-4">
            <Link
              to="/inquiry?destination=Dubai"
              className="btn-primary w-full justify-center"
              data-testid="dubai-dmc-bottom-cta-inquiry"
            >
              Request a custom proposal <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              className="btn-secondary w-full justify-center"
              data-testid="dubai-dmc-bottom-cta-contact"
            >
              Contact our Dubai team
            </Link>
          </div>
        </div>
      </section>

      {/* INTERNAL LINKS */}
      <section className="border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16">
          <div className="overline mb-6">Explore Trawelldmc</div>
          <nav
            className="grid grid-cols-2 md:grid-cols-4 gap-6"
            aria-label="Related Trawelldmc pages"
          >
            <Link
              to="/"
              className="group flex items-start gap-3"
              data-testid="dubai-link-home"
            >
              <Plane className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">
                  Home
                </div>
                <div className="text-sm text-slate-500 mt-1">
                  Back to Trawelldmc
                </div>
              </div>
            </Link>
            <Link
              to="/mice-services"
              className="group flex items-start gap-3"
              data-testid="dubai-link-mice-services"
            >
              <Trophy className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">
                  MICE Services
                </div>
                <div className="text-sm text-slate-500 mt-1">
                  Meetings, conferences, exhibitions
                </div>
              </div>
            </Link>
            <Link
              to="/incentive-travel"
              className="group flex items-start gap-3"
              data-testid="dubai-link-incentive-travel"
            >
              <Trophy className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">
                  Incentive Travel
                </div>
                <div className="text-sm text-slate-500 mt-1">
                  Reward your top performers
                </div>
              </div>
            </Link>
            <Link
              to="/corporate-travel"
              className="group flex items-start gap-3"
              data-testid="dubai-link-corporate-travel"
            >
              <Users className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">
                  Corporate Travel
                </div>
                <div className="text-sm text-slate-500 mt-1">
                  Tailored group programmes
                </div>
              </div>
            </Link>
          </nav>
        </div>
      </section>
    </div>
  );
}
